import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import { useMemo } from "react";

export interface TokenUsageData {
  current_tokens: number;
  max_tokens: number;
  percentage_used: number;
}

interface TokenUsageGaugeProps {
  tokenUsage: TokenUsageData | null;
  compact?: boolean;
  /** Display as a small circular pie chart */
  variant?: "linear" | "circular";
}

/**
 * Get color based on usage percentage
 * - Green (success): 0-60%
 * - Yellow (warning): 60-80%
 * - Red (error): 80-100%
 */
const getProgressColor = (
  percentage: number,
): "success" | "warning" | "error" => {
  if (percentage >= 80) return "error";
  if (percentage >= 60) return "warning";
  return "success";
};

/**
 * Format token count for display (e.g., 1500 -> "1.5k", 150000 -> "150k")
 */
const formatTokenCount = (count: number): string => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
  }
  return count.toString();
};

/**
 * TokenUsageGauge displays a linear progress bar showing model context window usage.
 * Shows current tokens used vs. maximum capacity with color-coded status.
 */
export const TokenUsageGauge = ({
  tokenUsage,
  compact = false,
  variant = "linear",
}: TokenUsageGaugeProps) => {
  const displayData = useMemo(() => {
    if (!tokenUsage) return null;

    const { current_tokens, max_tokens, percentage_used } = tokenUsage;
    const color = getProgressColor(percentage_used);
    const currentFormatted = formatTokenCount(current_tokens);
    const maxFormatted = formatTokenCount(max_tokens);

    return {
      current_tokens,
      max_tokens,
      percentage_used,
      color,
      currentFormatted,
      maxFormatted,
    };
  }, [tokenUsage]);

  if (!displayData) return null;

  const { percentage_used, color, currentFormatted, maxFormatted } =
    displayData;

  // Circular pie chart variant
  if (variant === "circular") {
    return (
      <Tooltip
        title={`Context: ${currentFormatted} / ${maxFormatted} tokens (${percentage_used.toFixed(
          1,
        )}%)`}
        arrow
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            {/* Background circle */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={18}
              thickness={5}
              sx={{ color: "action.disabledBackground" }}
            />
            {/* Foreground circle */}
            <CircularProgress
              variant="determinate"
              value={Math.min(percentage_used, 100)}
              size={18}
              thickness={5}
              color={color}
              sx={{ position: "absolute", left: 0 }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem" }}
          >
            {Math.round(percentage_used)}% used
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (compact) {
    return (
      <Tooltip
        title={`Context: ${currentFormatted} / ${maxFormatted} tokens (${percentage_used.toFixed(
          1,
        )}%)`}
        arrow
      >
        <Box
          sx={{ width: 80, display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage_used, 100)}
            color={color}
            sx={{ height: 4, borderRadius: 2, flex: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.65rem", minWidth: 28 }}
          >
            {Math.round(percentage_used)}%
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Context Window
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {currentFormatted} / {maxFormatted} tokens
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage_used, 100)}
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

/** Thresholds for context window warnings */
export const CONTEXT_WARNING_THRESHOLD = 80;
export const CONTEXT_CRITICAL_THRESHOLD = 90;

/** Check if context usage is at or above the critical threshold (input should be blocked) */
export const isContextExhausted = (
  tokenUsage: TokenUsageData | null | undefined,
): boolean => {
  if (!tokenUsage) return false;
  return tokenUsage.percentage_used >= CONTEXT_CRITICAL_THRESHOLD;
};

export default TokenUsageGauge;
